/**
 * 文件处理工具函数
 */

/**
 * 从指定URL下载文件
 * @param {string} url - 文件URL
 * @param {Object} wxInstance - 可选的微信实例，用于微信环境下的文件下载
 * @returns {Promise<Blob>} - 返回文件Blob对象
 */
export async function downloadFile(url, wxInstance = null) {
    try {
        // 如果提供了微信实例，则使用微信的下载方法
        if (wxInstance && typeof wxInstance.downloadFile === "function") {
            return await wxInstance.downloadFile(url);
        }

        // 否则使用标准的fetch方法
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`下载失败: ${response.status}`);
        }
        return await response.blob();
    } catch (error) {
        console.error("下载文件出错:", error);
        throw error;
    }
}

/**
 * 上传文件到后端
 * @param {Blob} blob - 文件Blob对象
 * @param {string} type - 文件类型
 * @param {Function} apiFn - API调用函数
 * @returns {Promise<Object>} - 返回上传结果
 */
export async function uploadFileToBackend(blob, type, apiFn) {
    try {
        const formData = new FormData();
        formData.append("file", blob);
        formData.append("type", type);

        // 使用传入的API方法上传文件
        const result = await apiFn("uploadMedia", formData);
        return result;
    } catch (error) {
        console.error("上传文件出错:", error);
        throw error;
    }
}

/**
 * 解析扫码结果
 * @param {string|Object} scanResult - 扫码结果，可以是字符串或包含result/resultStr属性的对象
 * @returns {string} - 返回解析后的扫码文本
 * @throws {Error} - 当扫码结果格式不正确时抛出错误
 */
export function parseScanResult(scanResult) {
    let scanText = "";
    if (typeof scanResult === "string") {
        scanText = scanResult;
    } else if (scanResult && (scanResult.result || scanResult.resultStr)) {
        scanText = scanResult.result || scanResult.resultStr;
    } else {
        throw new Error("扫码结果格式不正确");
    }

    // 解析扫码结果
    let wzy_server;
    if (scanText && scanText.startsWith("http")) {
        wzy_server = scanText;
        console.log("使用原始服务器地址:", wzy_server);
    } else if (scanText && scanText.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}:\d+$/)) {
        // 如果是IP:PORT格式，添加http前缀
        wzy_server = `http://${scanText}`;
        console.log("添加http前缀后的服务器地址:", wzy_server);
    } else {
        throw new Error("扫码结果无效，未获取到正确的服务器地址");
    }

    // 去除末尾斜杠以确保 URL 拼接正确
    if (wzy_server.endsWith("/")) {
        wzy_server = wzy_server.slice(0, -1);
    }

    return wzy_server;
}

/**
 * 从望诊仪获取数据并上传到后端
 * @param {string} wzy_server - 望诊仪服务器地址
 * @param {Function} apiFn - API调用函数
 * @param {Object} wxInstance - 可选的微信实例，用于微信环境下的文件下载
 * @returns {Promise<Object>} - 返回包含上传后URL的对象
 */
export async function fetchWZYDataAndUpload(wzy_server, apiFn, wxInstance = null) {
    // 构造后端可访问的 URL
    const face_url = `${wzy_server}/face.jpg`;
    const tongue_url = `${wzy_server}/tongue_surface.jpg`;
    const audio_url = `${wzy_server}/audio.wav`;

    // 从树莓派下载文件并上传到后端
    const faceBlob = await downloadFile(face_url, wxInstance);
    const faceUploadResult = await uploadFileToBackend(faceBlob, "face", apiFn);
    const uploadedFaceUrl = faceUploadResult.url;

    const tongueBlob = await downloadFile(tongue_url, wxInstance);
    const tongueUploadResult = await uploadFileToBackend(tongueBlob, "tongue", apiFn);
    const uploadedTongueUrl = tongueUploadResult.url;

    const audioBlob = await downloadFile(audio_url, wxInstance);
    const audioUploadResult = await uploadFileToBackend(audioBlob, "voice", apiFn);
    const uploadedAudioUrl = audioUploadResult.url;

    return {
        faceUrl: uploadedFaceUrl,
        tongueUrl: uploadedTongueUrl,
        audioUrl: uploadedAudioUrl,
    };
}
